import { z } from 'zod'
import {
  greetingCardDesignSchema,
  greetingCardSessionAssetReferenceSchema,
  greetingCardStoredAssetSchema,
  type GreetingCardLogoSlot,
  type GreetingCardVariant,
} from './schemas'
import {
  greetingCardPayloadDecorationSchema,
  greetingCardSessionPayloadSchema,
} from './builder-payload'
import {
  computeGreetingCardInsideTextBox,
  expandGreetingCardRect,
  getGreetingCardRotatedBounds,
} from './inside-text-box'
import {
  greetingCardSafeZonesToTemplateRects,
  greetingCardTextBlockingSafeZones,
  type LayoutRect,
} from './safe-zones'
import {
  getGreetingCardPaperRenderConfig,
  getGreetingCardPrintBorderPercentages,
} from './paper-sizes'
import {
  GREETING_CARD_PRINT_MANIFEST_PAYLOAD_KEY,
  GREETING_CARD_PRINT_MANIFEST_TYPE,
  GREETING_CARD_PRINT_MANIFEST_VERSION,
  greetingCardPrintManifestSchema,
  greetingCardPrintThemeSchema,
  type GreetingCardPrintManifest,
} from './print-manifest'

const DECORATION_GAP_PX = 24
const INSIDE_LOGO_GAP_PCT = 2
const MIN_TEXT_BOX_WIDTH_PX = 220
const MIN_TEXT_BOX_HEIGHT_PX = 120

type PrintAssetInput =
  | z.infer<typeof greetingCardSessionAssetReferenceSchema>
  | z.infer<typeof greetingCardStoredAssetSchema>
  | null
  | undefined

type PrintAssetReference = z.infer<typeof greetingCardSessionAssetReferenceSchema>

type BuildGreetingCardPrintManifestInput = {
  sessionId: string
  shop: string
  shopifyVariantId: string
  cardFormat: 'folded' | 'flat'
  paperSize: 'A5' | 'A6' | '4x6' | '5x7'
  theme: z.infer<typeof greetingCardPrintThemeSchema>
  templateSchema: Record<string, unknown>
  payload: Record<string, unknown>
  generatedAt?: Date
}

function formatSchemaIssues(issues: z.ZodIssue[]) {
  return issues
    .map((issue) => {
      const path = issue.path.join('.')
      return `${path || 'root'}: ${issue.message}`
    })
    .join(', ')
}

function getOptionalNonEmptyString(value: unknown) {
  if (typeof value !== 'string') return null
  const normalized = value.trim()
  return normalized.length > 0 ? normalized : null
}

function getResolvedInsideFontSizePt(input: {
  resolvedFontSizePt: unknown
  fontSizePt: number
}) {
  const { resolvedFontSizePt, fontSizePt } = input
  if (
    typeof resolvedFontSizePt !== 'number'
    || !Number.isFinite(resolvedFontSizePt)
    || resolvedFontSizePt <= 0
  ) {
    return null
  }

  return resolvedFontSizePt <= fontSizePt ? resolvedFontSizePt : null
}

function getResolvedInsideFontSizeRatio(input: {
  resolvedFontSizeRatio: unknown
  hasResolvedFontSizePt: boolean
}) {
  const { resolvedFontSizeRatio, hasResolvedFontSizePt } = input
  if (
    !hasResolvedFontSizePt
    || typeof resolvedFontSizeRatio !== 'number'
    || !Number.isFinite(resolvedFontSizeRatio)
    || resolvedFontSizeRatio <= 0
    || resolvedFontSizeRatio >= 1
  ) {
    return null
  }

  return resolvedFontSizeRatio
}

function getOptionalUrl(value: unknown) {
  const normalized = getOptionalNonEmptyString(value)
  if (!normalized || normalized.startsWith('data:')) return null
  return normalized
}

function toStoredAssetReference(value: unknown) {
  const parsed = greetingCardSessionAssetReferenceSchema.safeParse(value)
  if (!parsed.success) return null
  return parsed.data
}

/**
 * Converts design-level stored print masters into the private asset reference
 * shape used by print manifests and signed URL hydration.
 */
function toPrintAssetReference(value: PrintAssetInput): PrintAssetReference | null {
  const parsed = greetingCardSessionAssetReferenceSchema.safeParse(value)
  if (parsed.success) return parsed.data

  const storedAsset = greetingCardStoredAssetSchema.safeParse(value)
  if (!storedAsset.success) return null

  return {
    objectPath: storedAsset.data.objectPath,
    mimeType: storedAsset.data.mimeType,
    width: storedAsset.data.width,
    height: storedAsset.data.height,
    byteSize: storedAsset.data.byteSize,
  }
}

function resolveTemplateVariant(
  templateSchema: Record<string, unknown>,
  variantId: string,
) {
  const parsedTemplate = greetingCardDesignSchema.safeParse(templateSchema)
  if (!parsedTemplate.success) {
    throw new Error(
      `Invalid greeting card template schema for print manifest: ${formatSchemaIssues(parsedTemplate.error.issues)}`,
    )
  }

  const matchedVariant = parsedTemplate.data.variants.find((variant) => variant.id === variantId)
  if (!matchedVariant) {
    throw new Error(`Greeting card template schema does not include variant '${variantId}'`)
  }

  return {
    design: parsedTemplate.data,
    variant: matchedVariant,
  }
}

function normalizeFrontTextLayers(
  variant: GreetingCardVariant,
  payload: z.infer<typeof greetingCardSessionPayloadSchema>,
) {
  const frontTextValues = payload.frontTextLayers ?? {}
  const frontTextResolvedFontSizeRatios = payload.frontTextResolvedFontSizeRatios ?? {}
  return (variant.front.textLayers ?? []).map((layer) => {
    const resolvedFontSizeRatio = frontTextResolvedFontSizeRatios[layer.id]

    return {
      id: layer.id,
      label: getOptionalNonEmptyString(layer.label),
      text: frontTextValues[layer.id] ?? layer.defaultValue ?? '',
      bounds: layer.bounds ?? null,
      required: typeof layer.required === 'boolean' ? layer.required : null,
      maxLength: typeof layer.maxLength === 'number' ? layer.maxLength : null,
      defaultValue: getOptionalNonEmptyString(layer.defaultValue),
      defaultFontSize: typeof layer.defaultFontSize === 'number' ? layer.defaultFontSize : null,
      resolvedFontSizeRatio:
        typeof resolvedFontSizeRatio === 'number'
        && Number.isFinite(resolvedFontSizeRatio)
        && resolvedFontSizeRatio > 0
          ? resolvedFontSizeRatio
          : null,
      fontFamily: getOptionalNonEmptyString(layer.fontFamily),
      color: getOptionalNonEmptyString(layer.color),
      textAlign: getOptionalNonEmptyString(layer.textAlign),
      fontWeight: getOptionalNonEmptyString(layer.fontWeight),
    }
  })
}

function normalizeDecorations(
  side: 'outside' | 'inside',
  decorations: z.infer<typeof greetingCardPayloadDecorationSchema>[],
) {
  return decorations
    .map((decoration) => {
      if (!decoration.transform) return null

      return {
        id: getOptionalNonEmptyString(decoration.id),
        side,
        transform: decoration.transform,
        asset: {
          source: getOptionalNonEmptyString(decoration.asset.source),
          mimeType: getOptionalNonEmptyString(decoration.asset.mimeType),
          width: typeof decoration.asset.width === 'number' ? decoration.asset.width : null,
          height: typeof decoration.asset.height === 'number' ? decoration.asset.height : null,
          emoji: getOptionalNonEmptyString(decoration.asset.emoji),
          qrTargetUrl: getOptionalUrl(decoration.asset.qrTargetUrl),
          qrLabel: getOptionalNonEmptyString(decoration.asset.qrLabel),
          qrColorHex: getOptionalNonEmptyString(decoration.asset.qrColorHex),
          url: decoration.asset.storage
            ? null
            : getOptionalUrl(decoration.asset.src),
          storage: toStoredAssetReference(decoration.asset.storage),
        },
      }
    })
    .filter((decoration): decoration is NonNullable<typeof decoration> => decoration !== null)
}

function computeInsideTextBox(
  manifestDecorations: ReturnType<typeof normalizeDecorations>,
  safeAreaPx: { x: number, y: number, width: number, height: number },
  templatePx: { width: number, height: number },
  safeZones: NonNullable<GreetingCardVariant['inside']>['safeZones'],
  typography: GreetingCardPrintManifest['inside']['typography'],
  logoSlot: GreetingCardLogoSlot | null | undefined,
) {
  const contentTopPct = logoSlot?.bounds
    ? Math.min(100, (logoSlot.bounds.y + logoSlot.bounds.height) * 100 + INSIDE_LOGO_GAP_PCT)
    : 0
  const availableRect = {
    x: safeAreaPx.x,
    y: safeAreaPx.y + safeAreaPx.height * (contentTopPct / 100),
    width: safeAreaPx.width,
    height: safeAreaPx.height * Math.max(0, 1 - contentTopPct / 100),
  }

  const rectOverlapsAvailableTextArea = (rect: LayoutRect) => (
    rect.x < availableRect.x + availableRect.width
    && rect.y < availableRect.y + availableRect.height
    && rect.x + rect.width > availableRect.x
    && rect.y + rect.height > availableRect.y
  )
  const blockedRects = [
    ...manifestDecorations.map((decoration) =>
      expandGreetingCardRect(
        getGreetingCardRotatedBounds(decoration.transform),
        DECORATION_GAP_PX,
      ),
    ),
    ...greetingCardSafeZonesToTemplateRects(
      greetingCardTextBlockingSafeZones(safeZones),
      templatePx,
    ),
  ].filter(rectOverlapsAvailableTextArea)

  return computeGreetingCardInsideTextBox({
    availableRect,
    blockedRects,
    align: typography.align as 'left' | 'center' | 'right',
    vAlign: typography.vAlign as 'top' | 'middle' | 'bottom',
    minWidth: Math.min(MIN_TEXT_BOX_WIDTH_PX, availableRect.width),
    minHeight: Math.min(MIN_TEXT_BOX_HEIGHT_PX, availableRect.height),
  })
}

/**
 * Builds the server-owned print manifest Yuzu uses to render a saved card.
 */
export function buildGreetingCardPrintManifest(
  input: BuildGreetingCardPrintManifestInput,
) {
  const parsedPayload = greetingCardSessionPayloadSchema.safeParse(input.payload)
  if (!parsedPayload.success) {
    throw new Error(
      `Invalid greeting card payload for print manifest: ${formatSchemaIssues(parsedPayload.error.issues)}`,
    )
  }

  const { design, variant } = resolveTemplateVariant(
    input.templateSchema,
    parsedPayload.data.variantId,
  )

  const paper = getGreetingCardPaperRenderConfig(input.paperSize, design.orientation)
  let maxChars = 600
  if (typeof variant.inside?.maxChars === 'number') {
    maxChars = variant.inside.maxChars
  }
  if (typeof parsedPayload.data.inside?.maxChars === 'number') {
    maxChars = parsedPayload.data.inside.maxChars
  }
  const fontSizePt = typeof parsedPayload.data.inside?.fontSizePt === 'number'
    ? parsedPayload.data.inside.fontSizePt
    : 14
  const resolvedFontSizePt = getResolvedInsideFontSizePt({
    resolvedFontSizePt: parsedPayload.data.inside?.resolvedFontSizePt,
    fontSizePt,
  })
  const typography: GreetingCardPrintManifest['inside']['typography'] = {
    fontFamily: getOptionalNonEmptyString(parsedPayload.data.inside?.fontFamily),
    fontSizePt,
    resolvedFontSizePt,
    resolvedFontSizeRatio: getResolvedInsideFontSizeRatio({
      resolvedFontSizeRatio: parsedPayload.data.inside?.resolvedFontSizeRatio,
      hasResolvedFontSizePt: resolvedFontSizePt !== null,
    }),
    fontSource: parsedPayload.data.inside?.fontSource ?? null,
    colorHex: getOptionalNonEmptyString(parsedPayload.data.inside?.colorHex),
    align: parsedPayload.data.inside?.align ?? 'center',
    weight: parsedPayload.data.inside?.weight ?? 'regular',
    vAlign: parsedPayload.data.inside?.vAlign ?? 'middle',
    maxChars,
  }

  const outsideDecorations = normalizeDecorations(
    'outside',
    parsedPayload.data.decorations?.outside ?? [],
  )
  const insideDecorations = normalizeDecorations(
    'inside',
    parsedPayload.data.decorations?.inside ?? [],
  )

  const parsedTheme = greetingCardPrintThemeSchema.safeParse(input.theme)
  if (!parsedTheme.success) {
    throw new Error('Invalid greeting card print theme')
  }

  const manifestInput = {
    manifestType: GREETING_CARD_PRINT_MANIFEST_TYPE,
    manifestVersion: GREETING_CARD_PRINT_MANIFEST_VERSION,
    generatedAt: (input.generatedAt ?? new Date()).toISOString(),
    session: {
      id: input.sessionId,
      shop: input.shop,
    },
    card: {
      designId: design.id,
      designName: design.name,
      variantId: parsedPayload.data.variantId,
      shopifyVariantId: input.shopifyVariantId,
      orientation: design.orientation,
      cardFormat: input.cardFormat,
      paperSize: input.paperSize,
      supportedPaperSizes: design.supportedPaperSizes ?? [input.paperSize],
      frontImageUrl: getOptionalUrl(variant.front.preview?.frontUrl),
      thumbnailUrl: getOptionalUrl(variant.front.preview?.thumbUrl),
    },
    theme: parsedTheme.data,
    paper: {
      widthMm: paper.widthMm,
      heightMm: paper.heightMm,
      widthIn: paper.widthIn,
      heightIn: paper.heightIn,
      aspectRatio: paper.aspectRatio,
      templatePx: paper.templatePx,
      safeAreaPx: paper.safeAreaPx,
      printBorderPercentages: getGreetingCardPrintBorderPercentages(input.paperSize, design.orientation),
    },
    front: {
      type: variant.front.type,
      textFontPolicy: getOptionalNonEmptyString(variant.front.textFontPolicy),
      preview: {
        frontUrl: getOptionalUrl(variant.front.preview?.frontUrl),
        thumbUrl: getOptionalUrl(variant.front.preview?.thumbUrl),
      },
      printAsset: toPrintAssetReference(design.printAsset),
      background: variant.front.background ?? null,
      backdrop: variant.front.backdrop ?? null,
      logoSlot: variant.front.logoSlot ?? null,
      textLayers: normalizeFrontTextLayers(variant, parsedPayload.data),
      photo: variant.front.photo
        ? {
            slotBounds: variant.front.photo.slotBounds,
            maskUrl: getOptionalUrl(variant.front.photo.maskUrl),
            asset: toStoredAssetReference(parsedPayload.data.photo?.asset),
            placement: parsedPayload.data.photo?.placement ?? null,
          }
        : null,
      decorations: outsideDecorations,
    },
    inside: {
      defaultMessage: getOptionalNonEmptyString(variant.inside?.defaultMessage),
      text: parsedPayload.data.inside?.text ?? '',
      typography,
      logoSlot: variant.inside?.logoSlot ?? null,
      textBox: computeInsideTextBox(
        insideDecorations,
        paper.safeAreaPx,
        paper.templatePx,
        variant.inside?.safeZones,
        typography,
        variant.inside?.logoSlot,
      ),
      decorations: insideDecorations,
    },
    attachment: parsedPayload.data.attachment
      ? {
          type: parsedPayload.data.attachment.type,
          code: parsedPayload.data.attachment.code,
          playbackUrl: getOptionalUrl(parsedPayload.data.attachment.playbackUrl),
        }
      : null,
  }
  const manifest = greetingCardPrintManifestSchema.safeParse(manifestInput)
  if (!manifest.success) {
    throw new Error('Invalid greeting card print manifest')
  }

  return manifest.data
}

/**
 * Attaches a generated print manifest to the saved-card payload.
 */
export function attachGreetingCardPrintManifest(
  payload: Record<string, unknown>,
  manifest: GreetingCardPrintManifest,
) {
  return {
    ...payload,
    [GREETING_CARD_PRINT_MANIFEST_PAYLOAD_KEY]: manifest,
  }
}

/**
 * Removes the server-owned print manifest before a stored payload is reused as
 * editable builder state.
 */
export function stripGreetingCardPrintManifestFromPayload(payload: unknown) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return payload

  const {
    [GREETING_CARD_PRINT_MANIFEST_PAYLOAD_KEY]: _printManifest,
    ...editablePayload
  } = payload as Record<string, unknown>

  return editablePayload
}

/**
 * Reads and validates a server-owned print manifest from a saved payload.
 */
export function getGreetingCardPrintManifestFromPayload(payload: unknown) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null

  const manifest = (payload as Record<string, unknown>)[GREETING_CARD_PRINT_MANIFEST_PAYLOAD_KEY]
  const parsedManifest = greetingCardPrintManifestSchema.safeParse(manifest)
  if (!parsedManifest.success) return null
  return parsedManifest.data
}
